/** The field in play (docs/spec/04 §2): the hive, the fighter, everything in flight, the beams. */

import type { Beam, Empress, Enemy, GameState } from '../sim';
import { BEAM, FIELD, FIGHTER, NOVA, wingFrame } from '../sim';
import type { Motion } from './motion';
import { BLUE, CYAN, MAGENTA, RED, SKY, WHITE, YELLOW } from './palette';
import type { Screen } from './screen';
import type { SpriteId } from './sprites';
import { drawCapsule, drawSprite, MORPH_SPRITES, UP } from './sprites';

function enemySprite(e: Enemy): SpriteId {
  switch (e.kind) {
    case 'stinger': return 'stinger';
    case 'moth': return 'moth';
    case 'monarch': return e.hp >= 2 ? 'monarch' : 'monarchHit';
    case 'captive': return 'captive';
    case 'morph': return MORPH_SPRITES[e.variant % MORPH_SPRITES.length] ?? 'scarab';
  }
}

function drawEnemies(screen: Screen, s: GameState, m: Motion): void {
  const beat = wingFrame(s.tick);
  for (const e of s.enemies) {
    // A trio's followers are not on stage until their turn comes.
    if (e.gone || e.dist < 0) continue;
    const home = e.state === 'formed' || e.state === 'morphing';
    // In flight the wings beat four times as fast.
    const frame = home ? beat : Math.floor(s.tick / 8) % 2;
    let sprite = enemySprite(e);
    if (e.state === 'morphing' && Math.floor(e.timer / 4) % 2 === 1) {
      sprite = MORPH_SPRITES[Math.floor(e.timer / 8) % MORPH_SPRITES.length] ?? sprite;
    }
    const at = m.at(e, true);
    drawSprite(screen, sprite, frame, at.x, at.y, e.heading);
  }
}

const BEAM_COLOURS = [BLUE, SKY, CYAN, WHITE, CYAN, SKY];

/** Stripes of light running down the cone. */
function drawBeam(screen: Screen, beam: Beam, tick: number): void {
  const length = BEAM.length * Math.max(0, Math.min(1, beam.reach));
  for (let dy = 0; dy < length; dy += 2) {
    const rel = dy / BEAM.length;
    const half = BEAM.topHalf + (BEAM.bottomHalf - BEAM.topHalf) * rel;
    const band = Math.floor((dy - tick * 1.5) / 6);
    const colour = BEAM_COLOURS[((band % BEAM_COLOURS.length) + BEAM_COLOURS.length) % BEAM_COLOURS.length] ?? CYAN;
    // Each stripe is a bowed line: full width in the middle rows of a band, shorter at its edges.
    const bow = Math.abs(((dy - tick * 1.5) % 6 + 6) % 6 - 3) < 1.5 ? 0 : 2;
    screen.fill(beam.x - half + bow, beam.y + dy, (half - bow) * 2, 1, colour);
  }
}

function drawEmpress(screen: Screen, em: Empress, tick: number, m: Motion): void {
  const at = m.at(em, true);
  const x = at.x;
  const y = at.y;
  if (em.lance === 'warn') {
    // A thin line, flickering, where the lance will burn.
    if (tick % 4 < 2) screen.fill(em.lanceX, em.y + 14, 1, FIELD.h - em.y, RED);
  } else if (em.lance === 'fire') {
    const w = NOVA.lanceHalf * 2;
    const left = em.lanceX - NOVA.lanceHalf;
    screen.fill(left, em.y + 14, w, FIELD.h - em.y, tick % 2 === 0 ? MAGENTA : RED);
    screen.fill(left + 3, em.y + 14, w - 6, FIELD.h - em.y, tick % 2 === 0 ? WHITE : YELLOW);
  }
  const dying = em.state === 'dying';
  if (dying && tick % 4 < 2) return;
  drawSprite(screen, em.phase === 2 ? 'empressAngry' : 'empress', Math.floor(tick / 18), x, y);
  if (em.flash > 0) {
    const ctx = screen.ctx;
    const before = ctx.globalCompositeOperation;
    ctx.globalCompositeOperation = 'lighter';
    drawSprite(screen, 'empress', Math.floor(tick / 18), x, y);
    ctx.globalCompositeOperation = before;
  }
}

/** A ring of dots round a shielded ship. */
function drawShield(screen: Screen, x: number, y: number, tick: number): void {
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2 + tick * 0.06;
    screen.fill(x + Math.cos(a) * 12, y + Math.sin(a) * 11, 1, 1, i % 2 === 0 ? SKY : WHITE);
  }
}

function drawPlayer(screen: Screen, s: GameState, m: Motion): void {
  const p = s.player;
  if (p.status === 'caught') {
    // Turning as it rises, and turning the hive's colour.
    const sprite = p.timer > BEAM.rise / 2 && s.tick % 6 < 3 ? 'captive' : 'lancer';
    const at = m.caught(s);
    drawSprite(screen, sprite, 0, at.x, at.y, UP + p.spin);
    return;
  }
  if (p.status === 'dead') return;
  // Safe after a shield or a Nova: the ship blinks.
  if (p.safe > 0 && p.safe < 100_000 && s.tick % 4 < 2) return;
  const px = m.playerX(s);
  for (const x of p.dual ? [px - FIGHTER.dualOffset, px + FIGHTER.dualOffset] : [px]) {
    drawSprite(screen, 'lancer', 0, x, FIELD.fighterY);
    if (s.powers.shield) drawShield(screen, x, FIELD.fighterY, s.tick);
  }
}

/**
 * Draw the game's field. The caller draws the starfield before and the effects and HUD after.
 * `m` says where each thing is in this frame, between the last tick and the next.
 */
export function drawField(screen: Screen, s: GameState, m: Motion): void {
  if (s.beam) drawBeam(screen, s.beam, s.tick);
  if (s.empress) drawEmpress(screen, s.empress, s.tick, m);
  drawEnemies(screen, s, m);
  for (const b of s.bombs) {
    const at = m.at(b, true);
    drawSprite(screen, 'bomb', Math.floor(s.tick / 4), at.x, at.y);
  }
  for (const c of s.capsules) {
    const at = m.at(c);
    drawCapsule(screen, c.kind, at.x, at.y, c.t);
  }
  for (const shot of s.shots) {
    const at = m.at(shot);
    drawSprite(screen, shot.pierce ? 'shotPierce' : 'shot', 0, at.x, at.y, Math.atan2(shot.vy, shot.vx));
  }
  const r = s.rescue;
  if (r) {
    const at = m.at(r);
    drawSprite(screen, r.phase === 'spin' && s.tick % 6 < 3 ? 'captive' : 'lancer', 0, at.x, at.y, UP + r.spin);
  }
  drawPlayer(screen, s, m);
}
